import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { CalendarCheck2, Check, ChevronRight, Clock, X } from "lucide-react";
import { ReactNode } from "react";

import {
  ComplianceResponse,
  DailyCount,
  FullMeasurementCompliance,
} from "../../../api";
import { HelperTooltip } from "../../../components/ui-kit/base/helper-tooltip";
import { Tooltip } from "../../../components/ui-kit/base/tooltip";
import {
  Dialog,
  DialogBody,
  DialogHeader,
  DialogTrigger,
} from "../../../components/ui-kit/dialog";
import { cn } from "../../../utils/shadcn-ui";

// The tolerance is per schedule and often zero (the API clamps it to half the
// repeat period, so daily and weekly schedules get none), which is why this
// points at the dialog instead of naming a number.
const COMPLIANCE_TOOLTIP = msg`Share of scheduled full measurements the patient actually completed. Measurements within the schedule's tolerance still count, so a regular cadence on shifted days is not scored as missed. Click for the tolerance applied, the day-exact number and a daily breakdown.`;

const DAILY_COUNTS_TOOLTIP = msg`Day-exact: a session counts only on the day it was scheduled, with no tolerance for shifted days.`;

// The dialog's width: room for the daily grid.
const DIALOG_WIDTH = "480px";

const PILL_CLASS =
  "inline-flex items-center justify-center gap-1 min-w-12 rounded-md border border-border bg-muted px-3 py-1 text-xl font-bold leading-none";

/**
 * Colour band for the compliance bar, mirroring the risk-score classes in the
 * profile header. 80% is the adherence threshold medication-possession ratios
 * use, and below half the scheduled sessions is a problem to act on.
 */
function complianceBand(percent: number): { fill: string; frame: string } {
  if (percent >= 80)
    return {
      fill: "bg-success-soft",
      frame: "border-success-border text-success-foreground",
    };
  if (percent >= 50)
    return {
      fill: "bg-warning-soft",
      frame: "border-warning-border text-warning-foreground",
    };
  return {
    fill: "bg-danger-soft",
    frame: "border-danger-border text-danger-foreground",
  };
}

const SUMMARY_PILL_CLASS =
  "inline-flex items-center gap-1.5 rounded-md border border-border bg-muted px-2 py-1 text-xs text-muted-foreground";

/**
 * Compliance score for the profile header: the patient's `full_measurement`
 * soft compliance ratio (falling back to the overall weighted average) shown as
 * a bold percentage that opens a details dialog.
 */
export function ComplianceScore({
  compliance,
  compact = false,
  tooltip,
}: {
  compliance?: ComplianceResponse;
  compact?: boolean;
  tooltip?: ReactNode;
}) {
  if (compact) {
    if (!compliance) {
      const empty = (
        <span className={SUMMARY_PILL_CLASS}>
          <CalendarCheck2 className="size-3 shrink-0" />—
        </span>
      );
      return tooltip ? <Tooltip content={tooltip}>{empty}</Tooltip> : empty;
    }
    const full = getFullMeasurement(compliance);
    const { soft } = compliancePercents(compliance);
    const trigger = (
      <DialogTrigger asChild>
        <button
          type="button"
          className={`${SUMMARY_PILL_CLASS} cursor-pointer hover:shadow-sm`}
        >
          <CalendarCheck2 className="size-3 shrink-0" />
          {soft}%
        </button>
      </DialogTrigger>
    );
    return (
      <Dialog
        width={DIALOG_WIDTH}
        trigger={
          tooltip ? <Tooltip content={tooltip}>{trigger}</Tooltip> : trigger
        }
      >
        <ComplianceDialogContent compliance={compliance} full={full} />
      </Dialog>
    );
  }

  if (!compliance) {
    return (
      <span className={`${PILL_CLASS} w-full text-muted-foreground`}>—</span>
    );
  }

  const full = getFullMeasurement(compliance);
  const { soft } = compliancePercents(compliance);
  const { fill, frame } = complianceBand(soft);

  return (
    <Dialog
      width={DIALOG_WIDTH}
      trigger={
        <DialogTrigger asChild>
          <button
            type="button"
            className={cn(
              PILL_CLASS,
              "relative w-full cursor-pointer overflow-hidden",
              frame,
            )}
          >
            {/* The bar fills to the ratio it reports, so the row reads at a
              glance before the number does. */}
            <span
              className={cn("absolute inset-y-0 left-0", fill)}
              style={{ width: `${soft}%` }}
            />
            <span className="relative">{soft}%</span>
            <ChevronRight className="relative size-4" />
          </button>
        </DialogTrigger>
      }
    >
      <ComplianceDialogContent compliance={compliance} full={full} />
    </Dialog>
  );
}

/** Info icon + tooltip explaining the compliance score; rendered next to the label. */
export function ComplianceInfoTooltip() {
  const { t } = useLingui();
  return <HelperTooltip content={t(COMPLIANCE_TOOLTIP)} />;
}

type DayStatus = "performed" | "missed" | "pending";

const DAY_STATUS: Record<DayStatus, { icon: ReactNode; classes: string }> = {
  performed: {
    icon: <Check className="size-4" />,
    classes: "bg-success-soft border-success-border text-success-foreground",
  },
  missed: {
    icon: <X className="size-4" />,
    classes: "bg-danger-soft border-danger-border text-danger-foreground",
  },
  pending: {
    icon: <Clock className="size-4" />,
    classes: "bg-muted border-border text-muted-foreground",
  },
};

function ComplianceDialogContent({
  compliance,
  full,
}: {
  compliance: ComplianceResponse;
  full?: FullMeasurementCompliance;
}) {
  const { t } = useLingui();
  const { soft, hard, toleranceDays, differs } = compliancePercents(compliance);
  // The summary line follows the headline ratio and so counts soft; `missed`
  // below counts day-exact, like the daily grid. Keep the `completed` name —
  // Lingui hashes placeholder names into the message id, and renaming it would
  // drop the existing translations of a sentence that hasn't changed.
  const completed = full?.soft_total_complete_sessions ?? 0;
  const hardCompleted = full?.total_complete_sessions ?? 0;
  const expected = full?.total_expected ?? 0;
  const pending = full?.total_pending_sessions ?? 0;
  // Partial sessions count as missed (a partial measurement is invalid), so
  // missed is every due session that wasn't fully completed. `total_expected`
  // counts only due sessions — pending days report expected 0. This is the
  // day-exact count, matching the daily grid below.
  const missed = Math.max(0, expected - hardCompleted);
  const missing = distinctMissing(full?.most_common_missing ?? []);
  // The day-exact readouts only mean anything next to their explanation — the
  // tolerance line and the caption below — and both of those need the
  // full-measurement schedule. Without one we are on the overall averages,
  // which can differ with no tolerance to point at.
  const showDayExact = differs && full !== undefined;
  const dailyCounts = full?.daily_counts ?? [];

  return (
    <>
      {/* Spans, not divs: the description renders inside a <p>. */}
      <DialogHeader
        description={
          full && (
            <>
              <span className="block">
                <Trans>
                  {completed} out of {expected} sessions completed
                </Trans>
                {pending > 0 && ` · ${i18n._(msg`${pending} pending`)}`}
              </span>
              {toleranceDays > 0 && (
                <span className="block text-xs">
                  <Plural
                    value={toleranceDays}
                    one="Counted with a ±# day tolerance"
                    other="Counted with a ±# days tolerance"
                  />
                </span>
              )}
            </>
          )
        }
      >
        <Trans>Compliance details</Trans>
      </DialogHeader>

      <DialogBody className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <div
            className={`grid gap-2 ${showDayExact ? "grid-cols-3" : "grid-cols-2"}`}
          >
            <SummaryPill label={t`Compliance`} value={`${soft}%`} />
            {showDayExact && (
              <SummaryPill label={t`Day-exact`} value={`${hard}%`} />
            )}
            <SummaryPill label={t`Missed`} value={String(missed)} />
          </div>
          {/* Missed counts day-exact like the grid below, so with a tolerance
              in play it disagrees with the headline and has to say why. */}
          {showDayExact && (
            <div className="text-xs text-muted-foreground">
              <Trans>Missed and the daily breakdown below are day-exact.</Trans>
            </div>
          )}
        </div>

        {missing.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
              <Trans>Most common missing</Trans>
            </div>
            <div className="flex flex-col">
              {missing.map((name) => (
                <div key={name} className="capitalize py-0.5 text-xs">
                  {name}
                </div>
              ))}
            </div>
          </div>
        )}

        {dailyCounts.length > 0 && (
          <div>
            <div className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
              <Trans>Daily counts</Trans>
              <HelperTooltip content={t(DAILY_COUNTS_TOOLTIP)} />
            </div>
            <div className="flex items-center gap-3 text-2xs mb-2">
              <span className="flex items-center gap-1 text-success-foreground">
                <Check className="size-3.5" /> <Trans>Performed</Trans>
              </span>
              <span className="flex items-center gap-1 text-danger-foreground">
                <X className="size-3.5" /> <Trans>Missed</Trans>
              </span>
              <span className="flex items-center gap-1 text-muted-foreground">
                <Clock className="size-3.5" /> <Trans>Pending</Trans>
              </span>
            </div>
            <div className="grid grid-cols-7 gap-1 max-h-75 overflow-y-auto">
              {dailyCounts.map((dc) => (
                <DailyPill key={dc.date} day={dc} />
              ))}
            </div>
          </div>
        )}
      </DialogBody>
    </>
  );
}

function SummaryPill({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col items-center gap-1 rounded-md border border-border bg-muted px-3 py-2">
      <span className="text-2xs text-muted-foreground uppercase">{label}</span>
      <span className="text-2xl font-bold text-foreground leading-none">
        {value}
      </span>
    </div>
  );
}

function DailyPill({ day }: { day: DailyCount }) {
  const { icon, classes } = DAY_STATUS[dayStatus(day)];
  return (
    <div
      className={`flex flex-col items-center gap-0.5 rounded-md border px-1 py-1.5 ${classes}`}
    >
      <span className="flex h-4 items-center justify-center">{icon}</span>
      <span className="text-2xs leading-none">{shortDate(day.date)}</span>
    </div>
  );
}

function dayStatus(day: DailyCount): DayStatus {
  if (day.completed < day.expected) return "missed";
  if ((day.pending ?? 0) > 0) return "pending";
  return "performed";
}

/**
 * Distinct measurement names from `most_common_missing`, ordered most-frequent
 * first. The API returns a plain list of names (no counts, possibly with
 * repeats), so we dedupe by occurrence count.
 */
function distinctMissing(items: string[]): string[] {
  const counts = new Map<string, number>();
  for (const item of items) {
    counts.set(item, (counts.get(item) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([name]) => name);
}

function shortDate(date: string): string {
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("sk-SK", {
    day: "numeric",
    month: "numeric",
  }).format(d);
}

export function getFullMeasurement(
  compliance: ComplianceResponse,
): FullMeasurementCompliance | undefined {
  return compliance.schedules.find(
    (s): s is FullMeasurementCompliance =>
      s.schedule_type === "full_measurement",
  );
}

export type CompliancePercents = {
  /** Headline: frequency-tolerant, the clinically operative number. */
  soft: number;
  /** Day-exact adherence to the anchored schedule, for regulatory reporting. */
  hard: number;
  /**
   * Tolerance the API actually applied — it clamps the requested value to half
   * the schedule's repeat period, so daily and weekly schedules land on 0,
   * where soft collapses onto hard.
   */
  toleranceDays: number;
  /**
   * Whether the tolerance actually moved the number, i.e. whether the day-exact
   * one is worth showing at all. Compared on the rounded percents, so a gap too
   * small to see collapses to a single value.
   */
  differs: boolean;
};

/**
 * Both compliance ratios as whole percents: the full-measurement schedule's
 * when present, otherwise the overall weighted averages. This fallback is the
 * shared business rule behind every percentage the compliance UI renders.
 */
export function compliancePercents(
  compliance: ComplianceResponse,
): CompliancePercents {
  const full = getFullMeasurement(compliance);
  const soft = toPercent(
    full?.soft_compliance_ratio ?? compliance.overall_soft_compliance,
  );
  const hard = toPercent(
    full?.compliance_ratio ?? compliance.overall_compliance,
  );
  return {
    soft,
    hard,
    toleranceDays: full?.soft_tolerance_days ?? 0,
    differs: soft !== hard,
  };
}

function toPercent(ratio: number): number {
  return Math.round(ratio * 100);
}
